import { tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { detectDialCountry, toE164 } from "@/lib/phone";
import { CountryCodePicker } from "@/components/CountryCodePicker";
import { CountryPicker, useCountries } from "@/components/CountryPicker";

export const OPEN_RESELLER = "open-reseller-apply";
export const openResellerApply = () => window.dispatchEvent(new Event(OPEN_RESELLER));

const REQ = tx("reseller.this_field_is_required");
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const blank = (c) => ({ name: "", company: "", country: c, phone_country: c, phone_number: "", email: "", password: "", how_sell: "" });
const inp = (err) => `h-11 w-full rounded-xl border bg-white px-3.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-900 ${err ? "border-rose-400" : "border-slate-200"}`;

function validate(f) {
  const e = {};
  ["name", "phone_number", "email", "password", "how_sell"].forEach((k) => !f[k].trim() && (e[k] = REQ));
  if (!f.country) e.country = REQ;
  if (!e.email && !EMAIL_RE.test(f.email.trim())) e.email = tx("reseller.enter_a_valid_email");
  if (!e.phone_number && !toE164(f.phone_country, f.phone_number)) e.phone_number = tx("reseller.enter_a_valid_mobile_number");
  if (!e.password && f.password.length < 6) e.password = tx("reseller.at_least_6_characters");
  return e;
}

function Row({ label, err, id, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      {children}
      {err && <span className="block text-xs font-semibold text-rose-600" data-testid={`reseller-apply-error-${id}`}>{err}</span>}
    </label>
  );
}

function ApplyForm({ onDone }) {
  const countries = useCountries();
  const [f, setF] = useState(() => blank("PK"));
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { detectDialCountry().then((c) => setF((x) => (x.phone_number || x.name ? x : { ...x, country: c, phone_country: c }))); }, []);
  const set = (k) => (e) => { setF({ ...f, [k]: e?.target ? e.target.value : e }); setErr({ ...err, [k]: undefined }); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErr(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reseller/apply", { ...f, email: f.email.trim() });
      onDone(data.message);
    } catch (x) {
      setError(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="reseller-apply-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Row label={tx("reseller.full_name")} err={err.name} id="name"><input className={inp(err.name)} value={f.name} onChange={set("name")} data-testid="reseller-apply-name" /></Row>
        <Row label={tx("reseller.company_optional")} id="company"><input className={inp()} value={f.company} onChange={set("company")} data-testid="reseller-apply-company" /></Row>
      </div>
      <Row label={tx("reseller.country")} err={err.country} id="country"><CountryPicker value={f.country} countries={countries.filter((c) => c.code !== "OTHER")} onChange={set("country")} testid="reseller-apply-country" /></Row>
      <Row label={tx("reseller.mobile")} err={err.phone_number} id="phone_number">
        <div className="flex gap-2">
          <CountryCodePicker value={f.phone_country} onChange={set("phone_country")} />
          <input className={inp(err.phone_number)} inputMode="tel" value={f.phone_number} onChange={set("phone_number")} placeholder={tx("reseller.mobile_number")} data-testid="reseller-apply-phone" />
        </div>
      </Row>
      <div className="grid gap-4 sm:grid-cols-2">
        <Row label={tx("reseller.email")} err={err.email} id="email"><input type="email" className={inp(err.email)} value={f.email} onChange={set("email")} data-testid="reseller-apply-email" /></Row>
        <Row label={tx("reseller.password")} err={err.password} id="password"><input type="password" autoComplete="new-password" className={inp(err.password)} value={f.password} onChange={set("password")} data-testid="reseller-apply-password" /></Row>
      </div>
      <Row label={tx("reseller.how_will_you_sell_smart")} err={err.how_sell} id="how_sell">
        <textarea rows={4} className={`${inp(err.how_sell)} h-auto py-2.5`} value={f.how_sell} onChange={set("how_sell")} placeholder={tx("reseller.your_city_the_shops_you")} data-testid="reseller-apply-how" />
      </Row>
      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm font-medium text-rose-700" data-testid="reseller-apply-server-error">{error}</div>}
      <button type="submit" disabled={busy} data-testid="reseller-apply-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:bg-slate-300">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send application
      </button>
    </form>
  );
}

export function ResellerApplyDialog() {
  useContent();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => {
    const h = () => { setDone(""); setOpen(true); };
    window.addEventListener(OPEN_RESELLER, h);
    return () => window.removeEventListener(OPEN_RESELLER, h);
  }, []);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-xl" data-testid="reseller-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{txt("reseller.become_a_reseller")}</DialogTitle>
          <DialogDescription>{txt("reseller.earn_20_of_every_subscription")}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="reseller-apply-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <p className="font-display text-lg font-bold text-slate-900">{done}</p>
            <a href="/reseller" className="text-sm font-semibold text-slate-900 underline decoration-amber-500 decoration-2 underline-offset-4" data-testid="reseller-apply-login-link">{txt("reseller.go_to_reseller_login")}</a>
          </div>
        ) : <ApplyForm onDone={setDone} />}
      </DialogContent>
    </Dialog>
  );
}
